import React, { useEffect, useState, FC, useRef, useMemo } from "react";
import { Modal } from "react-bootstrap";
import { ITariffDetailsProps } from "../../types/propTypes";
import SearchPageService from "../../services/search-page/search-page-service";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import {
  IConnectorDetail,
  ITariffAvailable,
  ITariffBreakDown,
  ITariffComponent,
} from "../../models/search-page";
import StandardResponse from "../../models/standard-response";
import CountdownTimer from "../../components/CountDownTimer";
import "react-modern-drawer/dist/index.css";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import Slider from "react-slick";
import Drawer from "react-modern-drawer";
import RateCard from "./RateCard";
import { useTranslation } from "react-i18next";
import { useMediaQuery } from "react-responsive";
import "./style.css";

const TariffDetails: FC<ITariffDetailsProps> = (props) => {
  const { showModal, onHide, connectorId, details } = props;
  const navigate = useNavigate();
  const { t } = useTranslation();
  const searchPageService = new SearchPageService();

  const [holding, setHolding] = useState<boolean>(false);
  const [drawerOpen, setDrawerOpen] = useState<boolean>(true);

  const [currentSlide, setCurrentSlide] = useState(0);
  const sliderRef = useRef<Slider | null>(null);

  const [tariffComponent, setTariffComponent] = useState<ITariffComponent[]>(
    []
  );
  const [connectorDetails, setConnectorDetails] =
    useState<IConnectorDetail | null>(null);

  const [showTariffBreakdownModal, setShowTariffBreakdownModal] =
    useState<boolean>(false);
  const [showTooltip, setShowTooltip] = useState<boolean>(false);

  const isDesktop = useMediaQuery({ query: `(min-width: 768px)` });

  const hasFetched = useRef(false);

  useEffect(() => {
    if (!hasFetched.current) {
      getTariffBreakdownData();
      hasFetched.current = true;
    }
  }, []);

  const getTariffBreakdownData = async () => {
    await searchPageService
      .getTariffBreakDownDetails(connectorId)
      .then((response: StandardResponse<ITariffBreakDown>) => {
        if (response.Data && response.Data.active_components) {
          setTariffComponent(response.Data.active_components);
          setConnectorDetails(response.Data.connector_details);
        }
      })
      .catch((m) => {
        if (m.response && m.response.data && m.response.data.message) {
          toast.error(m.response.data.message);
        } else {
          toast.error(m.toString());
        }
      });
  };

  const startHold = () => {
    setHolding(true);
    setShowTooltip(false);
  };

  const stopHold = () => {
    setHolding(false);
  };

  const checkTariffAvailability = async () => {
    await searchPageService
      .checkIsTariffApplied(connectorId)
      .then((response: StandardResponse<ITariffAvailable>) => {
        if (response.Data.available) {
          navigate(`start-charge/${connectorId}`, {
            state: details,
          });
        }
      })
      .catch((m) => {
        if (m.response && m.response.data && m.response.data.message) {
          toast.error(m.response.data.message);
        } else {
          toast.error(m.toString());
        }
      })
      .finally(() => {
        stopHold();
      });
  };

  const renderComponent = () => {
    const slides = useMemo(() => {
      let tooltipAssigned = false;
      const flattened = tariffComponent.flatMap((item) =>
        item.price_breakdown
          .filter((p) => p.position === 1)
          .map((p) => {
            const qualifies = item.price_breakdown.length > 1;
            const isShowTooltip = !tooltipAssigned && qualifies;

            if (isShowTooltip) tooltipAssigned = true;

            return {
              ...p,
              type_label: item.type_label,
              unit: item.unit,
              image: item.image,
              isShowTooltip: isShowTooltip,
              otherPrices: item.price_breakdown.filter(
                (other) => other.position !== 1
              ),
              priceBreakdown: item.price_breakdown,
            };
          })
      );

      const elements: React.ReactElement[] = [];

      flattened.forEach((item, index) => {
        elements.push(
          <div
            key={`card-${index}`}
            style={{ minWidth: 120 }}
            className={
              index < flattened.length - 1
                ? `with-divider ${holding ? "plus-hold" : " "}`
                : ""
            }
          >
            <div className="ratecard-wrapper">
              <RateCard
                {...item}
                holding={holding}
                showTariffBreakdown={showTariffBreakdownModal}
                setShowTariffBreakdown={setShowTariffBreakdownModal}
                showTooltip={
                  showTooltip && item.isShowTooltip && currentSlide == index
                }
                setShowTooltip={setShowTooltip}
                cardIndex={index}
                currentSlide={currentSlide}
              />
            </div>
          </div>
        );
      });
      return elements;
    }, [tariffComponent, holding, showTooltip]);

    const settings = useMemo(
      () => ({
        dots: false,
        infinite: false,
        slidesToShow: 1,
        speed: 500,
        slidesToScroll: 1,
        variableWidth: true,
        mobileFirst: true,
        arrows: false,
        afterChange: (index: number) => {
          setCurrentSlide(index);
          setShowTooltip(false);
        },
        draggable: showTariffBreakdownModal ? false : true,
        swipe: showTariffBreakdownModal ? false : true,
        touchMove: showTariffBreakdownModal ? false : true,
      }),
      [showTariffBreakdownModal]
    );
    const totalSlides = slides.length;

    return (
      <div className={`tariff-details ${holding ? "blurred" : ""}`}>
        <div className="d-flex justify-content-between mb-3">
          {currentSlide > 0 ? (
            <img
              src={require("../../assets/images/arrow-left.svg").default}
              style={{ height: "20px", width: "20px" }}
              className="charging-icon"
              alt="tariff-component-icon"
              onClick={() => sliderRef.current?.slickPrev()}
            />
          ) : (
            <div style={{ height: "20px", width: "20px" }}></div>
          )}
          <div
            className="text-center flex-grow-1"
            style={{
              fontSize: "15px",
              color: "#404040",
              fontWeight: "bold",
            }}
          >
            {t<string>("stationDetails.tariffDetailsModal.currentRates")}
          </div>
          {currentSlide < totalSlides - 1 ? (
            <img
              src={require("../../assets/images/arrow-right.svg").default}
              style={{ height: "20px", width: "20px" }}
              className="charging-icon"
              alt="tariff-component-icon"
              onClick={() => sliderRef.current?.slickNext()}
            />
          ) : (
            <div style={{ height: "20px", width: "20px" }}></div>
          )}
        </div>

        <Slider ref={sliderRef} {...settings}>
          {slides}
        </Slider>
      </div>
    );
  };

  return (
    <div>
      {isDesktop ? (
        <Modal
          className={showTariffBreakdownModal ? "modal-fade-background" : ""}
          show={showModal}
          onHide={onHide}
        >
          <Modal.Header closeButton>
            <Modal.Title>
              {t<string>("stationDetails.tariffDetailsModal.tariffDetails")}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div>
              <div className="connector-details d-flex flex-column flex-md-row mb-3">
                <div className="connector-imagebackground">
                  <img
                    className="connector-image"
                    style={{ height: "25px" }}
                    src={connectorDetails?.image}
                    alt="connector"
                  />
                </div>
                <div className="d-flex flex-column justify-content-center">
                  <span>{connectorDetails?.name}</span>
                  {connectorDetails?.power && (
                    <span style={{ fontSize: "0.9em" }}>
                      {`${t(
                        "stationDetails.tariffDetailsModal.upto"
                      )} ${connectorDetails.power.replace(/\s*(kW)/i, "kW")}`}
                    </span>
                  )}
                </div>
              </div>
              {holding && (
                <div className="spinner">
                  <CountdownTimer
                    holding={holding}
                    onComplete={checkTariffAvailability}
                  />
                </div>
              )}
              {renderComponent()}
            </div>
          </Modal.Body>

          <Modal.Footer className="padding-bottom-10 mt-3">
            {details.charge_station_status.code === "AVAILABLE" && (
              <button
                className={`charging-button px-4 fw-bold w-100`}
                aria-label="Hold to Charge"
                onMouseDown={startHold}
                onTouchStart={startHold}
                onMouseUp={stopHold}
                onTouchEnd={stopHold}
                onMouseLeave={stopHold}
                onTouchCancel={stopHold}
                disabled={connectorId === 0}
              >
                {t<string>("stationDetails.button.holdToCharge")}
              </button>
            )}
          </Modal.Footer>
        </Modal>
      ) : (
        <div>
          <Drawer
            open={drawerOpen}
            onClose={onHide}
            direction="bottom"
            style={{
              height: "auto",
            }}
            size="80vh"
            className={`py-0 drawer ${
              showTariffBreakdownModal ? "modal-fade-background" : ""
            }`}
            lockBackgroundScroll={true}
          >
            <div
              className={`drawer-content ${
                showTariffBreakdownModal ? "blur-content" : ""
              }`}
            >
              <div className="drawer-header">
                <h3 style={{ fontWeight: "bold", margin: 0 }} className="mt-3">
                  {t<string>("stationDetails.tariffDetailsModal.tariffDetails")}
                </h3>
                <button
                  onClick={() => {
                    setDrawerOpen(false);
                    onHide();
                  }}
                  className="close-btn"
                  aria-label="Close"
                >
                  ×
                </button>
              </div>
              <div className="detailcontainer">
                <div className="connector-details mb-3">
                  <div className="connector-imagebackground">
                    <img
                      className="connector-image"
                      style={{ height: "25px" }}
                      src={connectorDetails?.image}
                      alt="connector"
                    />
                  </div>
                  <span className="mt-1">{connectorDetails?.name}</span>
                  <span>
                    {connectorDetails?.power && (
                      <span
                        style={{
                          fontSize: "0.9em",
                          marginLeft: 0,
                        }}
                      >
                        {`${t(
                          "stationDetails.tariffDetailsModal.upto"
                        )} ${connectorDetails.power.replace(/\s*(kW)/i, "kW")}`}
                      </span>
                    )}
                  </span>
                </div>
                {holding && (
                  <div className="spinner">
                    <CountdownTimer
                      holding={holding}
                      onComplete={checkTariffAvailability}
                    />
                  </div>
                )}

                {renderComponent()}
              </div>
              <div className="my-3">
                {details.charge_station_status.code === "AVAILABLE" && (
                  <button
                    className="charging-button px-4 fw-bold w-100"
                    aria-label="Hold to Charge"
                    onMouseDown={startHold}
                    onTouchStart={startHold}
                    onMouseUp={stopHold}
                    onTouchEnd={stopHold}
                    onMouseLeave={stopHold}
                    onTouchCancel={stopHold}
                    disabled={connectorId === 0}
                  >
                    {t<string>("stationDetails.button.holdToCharge")}
                  </button>
                )}
              </div>
            </div>
          </Drawer>
        </div>
      )}
    </div>
  );
};

export default TariffDetails;
